import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";

export default function KycAdmin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [selectedKyc, setSelectedKyc] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [filter, setFilter] = useState({ status: "", tier: "" });
  const [reviewForm, setReviewForm] = useState({ approved: true, rejectionReason: "", notes: "", upgradeToTier: null });

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [summaryRes, listRes] = await Promise.all([
        api.get("/Kyc/admin/summary"),
        api.get(`/Kyc/admin/list?status=${filter.status}&tier=${filter.tier}`)
      ]);
      setSummary(summaryRes.data);
      setSubmissions(listRes.data);
    } catch (err) {
      console.error("Error loading KYC data:", err);
      if (err.response?.status === 403) {
        alert("Access denied. Admin privileges required.");
        navigate("/admin");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadKycDetail = async (kycId) => {
    try {
      const res = await api.get(`/Kyc/admin/${kycId}`);
      setSelectedKyc(res.data);
      setShowDetailModal(true);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to load KYC details");
    }
  };

  const handleReview = async () => {
    if (!selectedKyc) return;

    try {
      await api.post(`/Kyc/admin/${selectedKyc.id}/review`, {
        approved: reviewForm.approved,
        rejectionReason: reviewForm.rejectionReason,
        notes: reviewForm.notes,
        upgradeToTier: reviewForm.upgradeToTier
      });
      alert(reviewForm.approved ? "✅ KYC Approved!" : "❌ KYC Rejected");
      setShowReviewModal(false);
      setShowDetailModal(false);
      setReviewForm({ approved: true, rejectionReason: "", notes: "", upgradeToTier: null });
      loadData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to submit review");
    }
  };

  const handleDocumentReview = async (documentId, approved, reason = "") => {
    try {
      await api.post("/Kyc/admin/documents/review", {
        documentId,
        approved,
        rejectionReason: reason
      });
      alert(approved ? "Document approved" : "Document rejected");
      loadKycDetail(selectedKyc.id);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to review document");
    }
  };

  const handleUpgradeTier = async (kycId, newTier) => {
    if (!window.confirm(`Upgrade this user to Tier ${newTier}?`)) return;
    try {
      await api.post(`/Kyc/admin/${kycId}/upgrade-tier?newTier=${newTier}`);
      alert(`✅ Upgraded to Tier ${newTier}`);
      loadData();
      if (selectedKyc) loadKycDetail(kycId);
    } catch (err) {
      alert(err.response?.data?.message || "Failed to upgrade tier");
    }
  };

  const formatCurrency = (amount) => `₦${(amount || 0).toLocaleString()}`;
  const formatDate = (date) => date ? new Date(date).toLocaleDateString("en-NG", { day: "2-digit", month: "short", year: "numeric" }) : "-";

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved": return "#16a34a";
      case "Rejected": return "#dc2626";
      case "InReview": return "#d97706";
      default: return "#6b7280";
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 1: return "#3b82f6";
      case 2: return "#8b5cf6";
      case 3: return "#16a34a";
      default: return "#6b7280";
    }
  };

  const styles = {
    container: { padding: "20px", maxWidth: "1400px", margin: "0 auto", fontFamily: "Arial, sans-serif" },
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" },
    title: { fontSize: "24px", fontWeight: "bold", color: "#1a1a2e" },
    backBtn: { padding: "10px 20px", background: "#6c757d", color: "white", border: "none", borderRadius: "8px", cursor: "pointer" },
    statsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "15px", marginBottom: "25px" },
    statCard: { background: "white", borderRadius: "12px", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", textAlign: "center" },
    statValue: { fontSize: "28px", fontWeight: "bold" },
    statLabel: { fontSize: "12px", color: "#6b7280", marginTop: "5px" },
    filterRow: { display: "flex", gap: "15px", marginBottom: "20px", flexWrap: "wrap", alignItems: "center" },
    select: { padding: "10px 15px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px" },
    card: { background: "white", borderRadius: "12px", padding: "20px", marginBottom: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" },
    table: { width: "100%", borderCollapse: "collapse" },
    th: { padding: "12px", textAlign: "left", borderBottom: "2px solid #e5e7eb", fontWeight: "600", color: "#374151", fontSize: "13px" },
    td: { padding: "12px", borderBottom: "1px solid #e5e7eb", fontSize: "14px" },
    badge: { padding: "4px 12px", borderRadius: "20px", fontSize: "11px", fontWeight: "600", display: "inline-block" },
    actionBtn: { padding: "6px 12px", border: "none", borderRadius: "6px", cursor: "pointer", fontSize: "12px", marginRight: "5px" },
    viewBtn: { background: "#e0f2fe", color: "#0369a1" },
    approveBtn: { background: "#dcfce7", color: "#16a34a" },
    rejectBtn: { background: "#fee2e2", color: "#dc2626" },
    modal: { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, overflow: "auto", padding: "20px" },
    modalContent: { background: "white", borderRadius: "12px", padding: "25px", width: "100%", maxWidth: "800px", maxHeight: "90vh", overflow: "auto" },
    modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", borderBottom: "1px solid #e5e7eb", paddingBottom: "15px" },
    modalTitle: { fontSize: "20px", fontWeight: "bold", color: "#1a1a2e" },
    closeBtn: { background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#6b7280" },
    detailGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "15px" },
    detailItem: { padding: "10px", background: "#f9fafb", borderRadius: "8px" },
    detailLabel: { fontSize: "11px", color: "#6b7280", textTransform: "uppercase", marginBottom: "4px" },
    detailValue: { fontSize: "14px", color: "#1a1a2e", fontWeight: "500" },
    sectionTitle: { fontSize: "16px", fontWeight: "bold", color: "#374151", marginTop: "20px", marginBottom: "15px", display: "flex", alignItems: "center", gap: "8px" },
    documentCard: { border: "1px solid #e5e7eb", borderRadius: "8px", padding: "12px", marginBottom: "10px", display: "flex", justifyContent: "space-between", alignItems: "center" },
    textarea: { width: "100%", padding: "12px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "14px", minHeight: "80px", resize: "vertical" },
    submitBtn: { padding: "12px 24px", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "600", fontSize: "14px" }
  };

  if (loading) {
    return (
      <div style={{ ...styles.container, textAlign: "center", paddingTop: "100px" }}>
        <div style={{ fontSize: "24px" }}>Loading KYC Management...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>🔐 KYC Management</h1>
        <button style={styles.backBtn} onClick={() => navigate("/admin")}>← Back to Admin</button>
      </div>

      {/* Summary Stats */}
      <div style={styles.statsGrid}>
        <div style={{ ...styles.statCard, borderTop: "4px solid #d97706" }}>
          <div style={{ ...styles.statValue, color: "#d97706" }}>{summary?.totalPending || 0}</div>
          <div style={styles.statLabel}>Pending</div>
        </div>
        <div style={{ ...styles.statCard, borderTop: "4px solid #3b82f6" }}>
          <div style={{ ...styles.statValue, color: "#3b82f6" }}>{summary?.totalInReview || 0}</div>
          <div style={styles.statLabel}>In Review</div>
        </div>
        <div style={{ ...styles.statCard, borderTop: "4px solid #16a34a" }}>
          <div style={{ ...styles.statValue, color: "#16a34a" }}>{summary?.totalApproved || 0}</div>
          <div style={styles.statLabel}>Approved</div>
        </div>
        <div style={{ ...styles.statCard, borderTop: "4px solid #dc2626" }}>
          <div style={{ ...styles.statValue, color: "#dc2626" }}>{summary?.totalRejected || 0}</div>
          <div style={styles.statLabel}>Rejected</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{summary?.tier1Count || 0}</div>
          <div style={styles.statLabel}>Tier 1</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{summary?.tier2Count || 0}</div>
          <div style={styles.statLabel}>Tier 2</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statValue}>{summary?.tier3Count || 0}</div>
          <div style={styles.statLabel}>Tier 3</div>
        </div>
      </div>

      {/* Filters */}
      <div style={styles.filterRow}>
        <select style={styles.select} value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
          <option value="">All Statuses</option>
          <option value="Pending">Pending</option>
          <option value="InReview">In Review</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
        </select>
        <select style={styles.select} value={filter.tier} onChange={(e) => setFilter({ ...filter, tier: e.target.value })}>
          <option value="">All Tiers</option>
          <option value="0">Tier 0 (Unverified)</option>
          <option value="1">Tier 1 (Basic)</option>
          <option value="2">Tier 2 (Standard)</option>
          <option value="3">Tier 3 (Enhanced)</option>
        </select>
        <button style={{ ...styles.actionBtn, ...styles.viewBtn }} onClick={loadData}>🔄 Refresh</button>
      </div>

      {/* Submissions Table */}
      <div style={styles.card}>
        <h3 style={{ marginBottom: "15px" }}>📋 KYC Submissions ({submissions.length})</h3>
        {submissions.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>User</th>
                  <th style={styles.th}>Email</th>
                  <th style={styles.th}>Tier</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Risk</th>
                  <th style={styles.th}>Submitted</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {submissions.map(kyc => (
                  <tr key={kyc.id}>
                    <td style={styles.td}><strong>{kyc.userFullName}</strong></td>
                    <td style={styles.td}>{kyc.userEmail}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: getTierColor(kyc.kycTier), color: "white" }}>
                        Tier {kyc.kycTier}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: `${getStatusColor(kyc.status)}20`, color: getStatusColor(kyc.status) }}>
                        {kyc.status}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, 
                        background: kyc.riskLevel === "High" ? "#fee2e2" : kyc.riskLevel === "Medium" ? "#fef3c7" : "#dcfce7",
                        color: kyc.riskLevel === "High" ? "#dc2626" : kyc.riskLevel === "Medium" ? "#d97706" : "#16a34a"
                      }}>
                        {kyc.riskLevel || "Low"}
                      </span>
                    </td>
                    <td style={styles.td}>{formatDate(kyc.submittedAt)}</td>
                    <td style={styles.td}>
                      <button style={{ ...styles.actionBtn, ...styles.viewBtn }} onClick={() => loadKycDetail(kyc.id)}>
                        👁️ View
                      </button>
                      {kyc.status === "InReview" && (
                        <>
                          <button style={{ ...styles.actionBtn, ...styles.approveBtn }} 
                            onClick={() => { loadKycDetail(kyc.id); setReviewForm({ ...reviewForm, approved: true }); setShowReviewModal(true); }}>
                            ✅
                          </button>
                          <button style={{ ...styles.actionBtn, ...styles.rejectBtn }}
                            onClick={() => { loadKycDetail(kyc.id); setReviewForm({ ...reviewForm, approved: false }); setShowReviewModal(true); }}>
                            ❌
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: "#6b7280", textAlign: "center", padding: "40px" }}>No KYC submissions found</p>
        )}
      </div>

      {/* Detail Modal */}
      {showDetailModal && selectedKyc && (
        <div style={styles.modal} onClick={() => setShowDetailModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>KYC Details - {selectedKyc.userFullName}</h3>
              <button style={styles.closeBtn} onClick={() => setShowDetailModal(false)}>×</button>
            </div>

            {/* Status Banner */}
            <div style={{ 
              padding: "15px", borderRadius: "8px", marginBottom: "20px",
              background: `${getStatusColor(selectedKyc.status)}15`,
              border: `1px solid ${getStatusColor(selectedKyc.status)}40`
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <span style={{ ...styles.badge, background: getTierColor(selectedKyc.kycTier), color: "white", marginRight: "10px" }}>
                    {selectedKyc.tierName}
                  </span>
                  <span style={{ ...styles.badge, background: `${getStatusColor(selectedKyc.status)}20`, color: getStatusColor(selectedKyc.status) }}>
                    {selectedKyc.status}
                  </span>
                </div>
                {selectedKyc.status === "Approved" && selectedKyc.kycTier < 3 && (
                  <button style={{ ...styles.actionBtn, ...styles.approveBtn }} 
                    onClick={() => handleUpgradeTier(selectedKyc.id, selectedKyc.kycTier + 1)}>
                    ⬆️ Upgrade to Tier {selectedKyc.kycTier + 1}
                  </button>
                )}
              </div>
              {selectedKyc.rejectionReason && (
                <p style={{ color: "#dc2626", marginTop: "10px", fontSize: "14px" }}>
                  <strong>Rejection Reason:</strong> {selectedKyc.rejectionReason}
                </p>
              )}
            </div>

            {/* Personal Information */}
            <h4 style={styles.sectionTitle}>👤 Personal Information</h4>
            <div style={styles.detailGrid}>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Full Name</div>
                <div style={styles.detailValue}>{selectedKyc.firstName} {selectedKyc.middleName} {selectedKyc.lastName}</div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Date of Birth</div>
                <div style={styles.detailValue}>{formatDate(selectedKyc.dateOfBirth)}</div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Gender</div>
                <div style={styles.detailValue}>{selectedKyc.gender || "-"}</div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Email</div>
                <div style={styles.detailValue}>{selectedKyc.email} {selectedKyc.emailVerified && "✅"}</div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Phone</div>
                <div style={styles.detailValue}>{selectedKyc.phoneNumber} {selectedKyc.phoneVerified && "✅"}</div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Nationality</div>
                <div style={styles.detailValue}>{selectedKyc.nationality || "Nigerian"}</div>
              </div>
            </div>

            {/* Verification Status */}
            <h4 style={styles.sectionTitle}>✅ Verification Status</h4>
            <div style={styles.detailGrid}>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>BVN</div>
                <div style={styles.detailValue}>{selectedKyc.bvn} {selectedKyc.bvnVerified ? "✅" : "❌"}</div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>NIN</div>
                <div style={styles.detailValue}>{selectedKyc.nin || "-"} {selectedKyc.ninVerified ? "✅" : ""}</div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>ID Document</div>
                <div style={styles.detailValue}>{selectedKyc.idDocumentType || "-"} {selectedKyc.idDocumentVerified ? "✅" : ""}</div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Address Verified</div>
                <div style={styles.detailValue}>{selectedKyc.addressVerified ? "✅ Yes" : "❌ No"}</div>
              </div>
            </div>

            {/* Address */}
            {selectedKyc.residentialAddress && (
              <>
                <h4 style={styles.sectionTitle}>🏠 Address</h4>
                <div style={styles.detailGrid}>
                  <div style={{ ...styles.detailItem, gridColumn: "1 / -1" }}>
                    <div style={styles.detailLabel}>Residential Address</div>
                    <div style={styles.detailValue}>{selectedKyc.residentialAddress}, {selectedKyc.city}, {selectedKyc.state}</div>
                  </div>
                </div>
              </>
            )}

            {/* Documents */}
            <h4 style={styles.sectionTitle}>📄 Documents</h4>
            {selectedKyc.documents?.length > 0 ? (
              selectedKyc.documents.map(doc => (
                <div key={doc.id} style={styles.documentCard}>
                  <div>
                    <strong>{doc.documentType.replace("_", " ")}</strong>
                    <span style={{ ...styles.badge, marginLeft: "10px",
                      background: doc.status === "Approved" ? "#dcfce7" : doc.status === "Rejected" ? "#fee2e2" : "#fef3c7",
                      color: doc.status === "Approved" ? "#16a34a" : doc.status === "Rejected" ? "#dc2626" : "#d97706"
                    }}>
                      {doc.status}
                    </span>
                    <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                      Uploaded: {formatDate(doc.uploadedAt)}
                    </div>
                  </div>
                  <div>
                    <a href={doc.documentUrl} target="_blank" rel="noopener noreferrer" 
                      style={{ ...styles.actionBtn, ...styles.viewBtn, textDecoration: "none" }}>
                      👁️ View
                    </a>
                    {doc.status === "Pending" && (
                      <>
                        <button style={{ ...styles.actionBtn, ...styles.approveBtn }} 
                          onClick={() => handleDocumentReview(doc.id, true)}>✅</button>
                        <button style={{ ...styles.actionBtn, ...styles.rejectBtn }}
                          onClick={() => {
                            const reason = prompt("Rejection reason:");
                            if (reason) handleDocumentReview(doc.id, false, reason);
                          }}>❌</button>
                      </>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p style={{ color: "#6b7280" }}>No documents uploaded</p>
            )}

            {/* Transaction Limits */}
            <h4 style={styles.sectionTitle}>💰 Transaction Limits</h4>
            <div style={styles.detailGrid}>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Daily Limit</div>
                <div style={styles.detailValue}>{formatCurrency(selectedKyc.dailyTransactionLimit)}</div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Single Transaction</div>
                <div style={styles.detailValue}>{formatCurrency(selectedKyc.singleTransactionLimit)}</div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Maximum Balance</div>
                <div style={styles.detailValue}>{formatCurrency(selectedKyc.maximumBalance)}</div>
              </div>
            </div>

            {/* Risk Assessment */}
            <h4 style={styles.sectionTitle}>⚠️ Risk Assessment</h4>
            <div style={styles.detailGrid}>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Risk Level</div>
                <div style={styles.detailValue}>{selectedKyc.riskLevel || "Low"}</div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>PEP Status</div>
                <div style={styles.detailValue}>{selectedKyc.isPep ? "⚠️ Yes" : "No"}</div>
              </div>
              <div style={styles.detailItem}>
                <div style={styles.detailLabel}>Watchlisted</div>
                <div style={styles.detailValue}>{selectedKyc.isWatchlisted ? "⚠️ Yes" : "No"}</div>
              </div>
            </div>

            {/* Action Buttons */}
            {selectedKyc.status === "InReview" && (
              <div style={{ marginTop: "20px", display: "flex", gap: "10px" }}>
                <button style={{ ...styles.submitBtn, background: "#16a34a", color: "white", flex: 1 }}
                  onClick={() => { setReviewForm({ ...reviewForm, approved: true }); setShowReviewModal(true); }}>
                  ✅ Approve KYC
                </button>
                <button style={{ ...styles.submitBtn, background: "#dc2626", color: "white", flex: 1 }}
                  onClick={() => { setReviewForm({ ...reviewForm, approved: false }); setShowReviewModal(true); }}>
                  ❌ Reject KYC
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && selectedKyc && (
        <div style={styles.modal} onClick={() => setShowReviewModal(false)}>
          <div style={{ ...styles.modalContent, maxWidth: "500px" }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>{reviewForm.approved ? "✅ Approve" : "❌ Reject"} KYC</h3>
              <button style={styles.closeBtn} onClick={() => setShowReviewModal(false)}>×</button>
            </div>

            <p style={{ marginBottom: "15px" }}>
              {reviewForm.approved 
                ? `Approve KYC for ${selectedKyc.userFullName}?` 
                : `Reject KYC for ${selectedKyc.userFullName}?`}
            </p>

            {reviewForm.approved && (
              <div style={{ marginBottom: "15px" }}>
                <label style={{ fontWeight: "600", display: "block", marginBottom: "5px" }}>Upgrade to Tier (Optional)</label>
                <select style={styles.select} value={reviewForm.upgradeToTier || ""}
                  onChange={(e) => setReviewForm({ ...reviewForm, upgradeToTier: e.target.value ? parseInt(e.target.value) : null })}>
                  <option value="">Auto-determine</option>
                  <option value="1">Tier 1 (Basic)</option>
                  <option value="2">Tier 2 (Standard)</option>
                  <option value="3">Tier 3 (Enhanced)</option>
                </select>
              </div>
            )}

            {!reviewForm.approved && (
              <div style={{ marginBottom: "15px" }}>
                <label style={{ fontWeight: "600", display: "block", marginBottom: "5px" }}>Rejection Reason *</label>
                <textarea style={styles.textarea} placeholder="Explain why the KYC is being rejected..."
                  value={reviewForm.rejectionReason}
                  onChange={(e) => setReviewForm({ ...reviewForm, rejectionReason: e.target.value })} />
              </div>
            )}

            <div style={{ marginBottom: "15px" }}>
              <label style={{ fontWeight: "600", display: "block", marginBottom: "5px" }}>Internal Notes</label>
              <textarea style={styles.textarea} placeholder="Optional notes for internal reference..."
                value={reviewForm.notes}
                onChange={(e) => setReviewForm({ ...reviewForm, notes: e.target.value })} />
            </div>

            <div style={{ display: "flex", gap: "10px" }}>
              <button style={{ ...styles.submitBtn, background: reviewForm.approved ? "#16a34a" : "#dc2626", color: "white", flex: 1 }}
                onClick={handleReview} disabled={!reviewForm.approved && !reviewForm.rejectionReason}>
                {reviewForm.approved ? "✅ Approve" : "❌ Reject"}
              </button>
              <button style={{ ...styles.submitBtn, background: "#6b7280", color: "white" }}
                onClick={() => setShowReviewModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
